import { isFetchError } from "@repo/api-sdk";
import { useMemo } from "react";

import {
  claimedSellerIds,
  GUEST_CLAIMS,
  UNKNOWN_CLAIMS,
  type ClaimedSellers
} from "@/utils/offerPriceState";

import { useProfile } from "./customer";
import { useSupplierMatches } from "./supplier-match";

/**
 * The sellers the logged-in salon has a claimed Supplier Match with (KAN-343).
 *
 * A guest is `guest` - the profile 401s, which is the app's logged-out signal.
 * A logged-in salon is `unknown` until its Supplier Match grid has been read,
 * and `known` after. A profile that failed for any reason other than 401, or a
 * grid that failed to load, stays `unknown` rather than being read as "no
 * claims": claiming nothing is the one answer that would let a retail price
 * through.
 */
export function useClaimedSellers(): ClaimedSellers {
  const profile = useProfile();
  const isGuest =
    profile.status === "error" &&
    isFetchError(profile.error) &&
    profile.error.status === 401;

  const matches = useSupplierMatches({ enabled: profile.status === "success" });

  const matchRows = matches.data?.supplier_matches;

  return useMemo(() => {
    if (isGuest) return GUEST_CLAIMS;
    if (profile.status !== "success" || !matchRows) return UNKNOWN_CLAIMS;
    return { kind: "known", ids: claimedSellerIds(matchRows) };
  }, [isGuest, profile.status, matchRows]);
}
